import type { CatalogItem } from '../api';
import { FieldGroup } from '../components/form';
import { Badge, Switch } from '../components/ui';
import { findItem, isSelected, toggleItem, updateItem, type Draft } from './draft';

/**
 * The generic picker behind hardware, systems, remote access and SharePoint
 * sites: four sections of the paper form that differ only in which extra field
 * they need alongside the tick.
 */
export function StepCatalog({
  title,
  description,
  entries,
  draft,
  onChange,
  showAssetDetails = false,
  showNotes = false,
  alreadyGranted,
}: {
  title: string;
  description: string;
  entries: CatalogItem[];
  draft: Draft;
  onChange: (next: Draft) => void;
  showAssetDetails?: boolean;
  showNotes?: boolean;
  /** "More access" only: items the original enrolment granted, locked out here. */
  alreadyGranted?: Map<string, 'R' | 'RW' | null>;
}) {
  const selectedCount = entries.filter((entry) => isSelected(draft, entry.id)).length;
  const held = (entry: CatalogItem) => alreadyGranted?.has(entry.id) ?? false;
  const compulsoryHere = (entry: CatalogItem) => entry.isCompulsory && !alreadyGranted;

  return (
    <FieldGroup title={title} description={description}>
      <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
        {selectedCount} of {entries.length} selected
      </p>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {entries.map((entry) => {
          const on = isSelected(draft, entry.id);
          const item = findItem(draft, entry.id);
          const needsDetails = showAssetDetails && entry.requiresAssetDetails;
          const has = held(entry);

          return (
            <li key={entry.id} className={has ? 'py-3.5 opacity-70' : 'py-3.5'}>
              <div className="flex items-start gap-3">
                <Switch
                  id={`catalogue-${entry.id}`}
                  checked={has || on || compulsoryHere(entry)}
                  disabled={has || compulsoryHere(entry)}
                  onChange={(next) => onChange(toggleItem(draft, alreadyGranted ? { ...entry, isCompulsory: false } : entry, next))}
                  label={entry.name}
                />
                <div className="min-w-0 flex-1">
                  <label
                    htmlFor={`catalogue-${entry.id}`}
                    className="block cursor-pointer text-sm font-medium text-slate-800 dark:text-slate-200"
                  >
                    {entry.name}
                    {has && (
                      <span className="ml-2 align-middle">
                        <Badge tone="success">Already has</Badge>
                      </span>
                    )}
                    {compulsoryHere(entry) && (
                      <span className="ml-2 align-middle">
                        <Badge tone="info">Everyone gets this</Badge>
                      </span>
                    )}
                  </label>
                  {entry.description && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{entry.description}</p>
                  )}

                  {on && (needsDetails || showNotes) && (
                    <input
                      type="text"
                      value={(needsDetails ? item?.assetDetails : item?.notes) ?? ''}
                      onChange={(event) =>
                        onChange(
                          updateItem(draft, entry.id,
                            needsDetails ? { assetDetails: event.target.value } : { notes: event.target.value },
                          ),
                        )
                      }
                      placeholder={needsDetails ? 'Brand, model or serial number' : 'Notes for IT (optional)'}
                      maxLength={needsDetails ? 200 : 500}
                      className="mt-2 w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-500 dark:placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </FieldGroup>
  );
}
